import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

interface Option {
  value: string;
  label: string;
}

interface CustomSelectProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  required?: boolean;
  searchable?: boolean;
}

const inputVariants = {
  focus: { scale: 1.02, transition: { duration: 0.2 } },
  blur: { scale: 1, transition: { duration: 0.2 } },
};

const labelVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.2 } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.15 } },
};

const CustomSelect: React.FC<CustomSelectProps> = ({
  name,
  value,
  onChange,
  options,
  placeholder = "Select an option",
  required = false,
  searchable = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const displayRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    setInputValue(selectedOption ? selectedOption.label : "");
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsFocused(false);
        setSearchMode(false);
        setInputValue(selectedOption ? selectedOption.label : "");
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedOption]);

  const filteredOptions = searchMode
    ? options.filter(opt =>
        opt.label.toLowerCase().includes(inputValue.toLowerCase())
      )
    : options;

  const handleInputFocus = () => {
    setIsOpen(true);
    setIsFocused(true);
    setSearchMode(true);
    setInputValue("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };
  // const handleFieldClick = (e?: React.MouseEvent) => {
  //   if (e) e.stopPropagation();
  //   if (searchable) {
  //     inputRef.current?.focus();
  //     setIsOpen(true);
  //   } else {
  //     setIsOpen((prev) => !prev);
  //     setIsFocused((prev) => !prev);
  //   }
  // };

  const handleContainerClick = () => {
    if (!isOpen) {
      setIsOpen(true);
      setIsFocused(true);
      if (searchable) {
        setSearchMode(true);
        setInputValue("");
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    } else if (searchable) {
      setSearchMode(true);
      inputRef.current?.focus();
    }
  };

  const handleArrowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(prev => !prev);
    setIsFocused(prev => !prev);
    if (searchable && !isOpen) {
      setSearchMode(true);
      setInputValue("");
      inputRef.current?.focus();
    } else {
      setSearchMode(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setSearchMode(true);
  };

  const handleSelect = (opt: Option) => {
    onChange(opt.value);
    setIsOpen(false);
    setIsFocused(false);
    setSearchMode(false);
    setInputValue(opt.label);
  };

  return (
    <div className="relative w-[200px]" ref={dropdownRef}>
      <motion.div
        className={`flex items-center border border-inkBlack w-full px-4 py-3 rounded-md bg-white cursor-pointer text-base focus-within:border-transparent focus-within:outline-none focus-within:ring-2 focus-within:ring-orange min-w-[200px] justify-between`}
        whileFocus="focus"
        variants={inputVariants}
        initial="blur"
        animate={isFocused ? "focus" : "blur"}
        tabIndex={-1}
        onClick={handleContainerClick}
      >

        {/* Content area - fixed width, no expansion */}
        <div className="flex-1 min-w-0 overflow-hidden relative">
          {/* Always visible display of selected value */}
          <div 
            ref={displayRef}
            className={`${!searchMode && selectedOption ? 'text-gray-500' : 'hidden'} truncate`}
            title={selectedOption?.label}
          >
            {selectedOption?.label}
          </div>
          {searchMode && (
            // <input
            //   ref={inputRef}
            //   type="text"
            //   name={name + "_search"}
            //   value={inputValue}
            //   onFocus={handleInputFocus}
            //   onChange={handleInputChange}
            //   className="bg-transparent outline-none border-none focus:border-none focus:ring-0 p-0 m-0 text-base text-black placeholder-gray-500"
            //   autoComplete="off"
            //   placeholder={selectedOption?.label || placeholder + (required ? '*' : '')}
            //   style={{ cursor: 'text' }}
            // />
            <div className="relative w-full overflow-hidden">
            <input
              ref={inputRef}
              type="text"
              name={name + "_search"}
              value={inputValue}
              onFocus={handleInputFocus}
              onChange={handleInputChange}
              className="bg-transparent outline-none border-none focus:ring-0 p-0 m-0 text-base text-black w-full truncate"
              autoComplete="off"
              required={false} // because you're using a hidden field for form value
              // placeholder={searchMode ? placeholder + (required ? "*" : "") : ""}
              style={{ cursor: "text" }}
            />
            {/* Fake placeholder (when not typing and value exists) */}
            {inputValue === "" && selectedOption && (
              <div
                className="absolute inset-0 px-0 py-0 flex items-center text-gray-500 pointer-events-none truncate"
              >
                {selectedOption.label}
              </div>
            )}
          </div>
          )}

          {/* Placeholder - only visible when no value is selected and not searching */}
          {/* {!selectedOption && !searchMode && (
            <div className="text-gray-500 truncate">
              {placeholder}
              {required && <span className="text-orange"> *</span>}
            </div>
          )} */}
        </div>

        <ChevronDown 
          onClick={handleArrowClick} 
          className={`flex-shrink-0 h-5 w-5 text-gray-400 transition-transform duration-200 ml-2 ${isOpen ? "rotate-180" : ""}`} 
        />
      </motion.div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="absolute z-10 w-full bg-white border border-orange-500 rounded-md shadow-lg max-h-48 overflow-y-auto mt-2"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
          >
            {(searchMode ? filteredOptions : options).map(opt => (
              <button
                key={opt.value}
                className={`block w-full text-left px-4 py-2 text-base transition-colors
                  ${opt.value === value ? '' : 'bg-white text-black hover:bg-orange-100'}`}
                onMouseDown={e => e.preventDefault()}
                onClick={() => handleSelect(opt)}
                type="button"
                style={opt.value === value ? { backgroundColor: '#FFEDD5', color: '#FF6B00' } : {}}
               aria-label="Previous">
                {opt.label}
              </button>
            ))}
            {searchMode && filteredOptions.length === 0 && (
              <div className="px-4 py-3 text-gray-400 text-base">No options found</div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isFocused && (
          <motion.span
            className="absolute text-xs capitalize text-black -top-2 left-2 bg-white px-1 pointer-events-none"
            variants={labelVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {placeholder}
            {required && <span className="text-orange"> *</span>}
          </motion.span>
        )}
      </AnimatePresence>

      <input type="hidden" name={name} value={value} required={required} />
    </div>
  );
};

export default CustomSelect;